/**
 * Layout for public pages (login, register, forgot, reset, verify, etc.).
 *
 * Design: centered card on a soft background, language switcher top-right,
 * subtle product wordmark top-left. Apple-landing-page feeling.
 */

import { Outlet } from "react-router";
import { ScrollToTop } from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/lib/i18n";
import { cn } from "@/lib/cn";

export default function PublicLayout() {
  const { t, i18n } = useTranslation();

  return (
    <div className="relative min-h-screen bg-surface-base">
      {/* Header */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-baseline gap-2">
          <span className="text-title font-semibold tracking-[-0.01em] text-ink">
            {t("common.app_name")}
          </span>
        </div>

        <div className="flex items-center gap-1 rounded-pill bg-surface-subtle p-1">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isActive = i18n.resolvedLanguage === lang;
            return (
              <button
                key={lang}
                type="button"
                onClick={() => i18n.changeLanguage(lang)}
                className={cn(
                  "rounded-pill px-3 py-1 text-micro font-medium uppercase tracking-wider transition-colors focus:outline-none focus-visible:shadow-focus",
                  isActive
                    ? "bg-surface-card text-ink shadow-xs"
                    : "text-ink-subtle hover:text-ink",
                )}
                aria-pressed={isActive}
              >
                {lang}
              </button>
            );
          })}
        </div>
      </header>

      {/* Main */}
      <main className="mx-auto flex min-h-[calc(100vh-200px)] max-w-md items-center px-6 pb-12 pt-4">
        <div className="w-full animate-fade-in">
          <ScrollToTop />
        <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="mx-auto max-w-6xl px-6 py-6 text-center">
        <p className="text-micro text-ink-subtle">
          © {new Date().getFullYear()} sendSMS Hub
        </p>
      </footer>
    </div>
  );
}
